// src/components/UmatMappingQiuDao.js
import React, { useEffect, useState, useRef } from "react";
import LookupUmat from "./LookupUmat";
import { apiFetch, getUmatByEmail } from "../utils/wkCommon";
import { getUser } from "../utils/auth";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import PERMISSIONS from "../utils/wkCommon";

const rem = (px) => `${px / 16}rem`;

export default function UmatMappingQiuDao({ onSaved, umatId }) {
  const user = getUser();

  // permissions: follow UmatUpdateIndividual pattern
  const [canEdit, setCanEdit] = useState(false);
  const [canEditAndApply, setCanEditAndApply] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadPerms() {
      try {
        const email = user?.email;
        if (!email) {
          if (mounted) { setCanEdit(false); setCanEditAndApply(false); }
          return;
        }
        const pEdit = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_EDIT);
        const pEditApproval = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_EDITAPPROVAL);
        if (mounted) {
          setCanEdit(Boolean(pEdit));
          setCanEditAndApply(Boolean(pEditApproval));
        }
      } catch (err) {
        console.warn("loadPerms error", err);
        if (mounted) { setCanEdit(false); setCanEditAndApply(false); }
      }
    }
    loadPerms();
    return () => { mounted = false; };
  }, [user]);

  const [selectedUmat, setSelectedUmat] = useState(null); // object from LookupUmat
  const [searchTerm, setSearchTerm] = useState("");
  const [qiuResults, setQiuResults] = useState([]);
  const [selectedQiu, setSelectedQiu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // {type, text}
  const [error, setError] = useState(null);
  const [lockedKodeUmat, setLockedKodeUmat] = useState(null);
  const [selected, setSelected] = useState(null);

  const [submitState, setSubmitState] = useState("idle");
  // "idle" | "submitting" | "success" | "error"
  const qiuBoxRef = useRef(null);


  // If selectedUmat already linked to qiu_dao_person -> disallow mapping
  // Note: LookupUmat returns either raw object or attributes wrapper; be defensive
  const attrs = selectedUmat?.attributes ?? selectedUmat;
  const alreadyLinked = attrs?.qiu_dao_person ?? attrs?.qiu_dao_person?.data ?? null;

  useEffect(() => {
    if (!umatId) return;

    async function loadUmat() {
      try {
        const resp = await apiFetch(`/api/umats/${umatId}?populate=area`);
        const u = resp?.data ?? null;

        if (!u) return;

        const normalized = u.attributes
          ? { id: u.id, ...u.attributes }
          : u;

        setLockedKodeUmat(normalized.umatID ?? umatId);
        setSelected(normalized);
        setSelectedUmat(normalized);

      } catch (err) {
        console.error("load umat failed", err);
      }
    }

    loadUmat();

  }, [umatId]);
  
  // reset when lookup cleared
  useEffect(() => {
    if (!selectedUmat) {
      setSearchTerm("");
      setQiuResults([]);
      setSelectedQiu(null);
      return;
    }

    if (alreadyLinked) {
      setError("Umat ini sudah terhubung ke QiuDao-person dan tidak bisa dipilih lagi.");
      // clear selection in component state (but we cannot force LookupUmat; assume parent passes value prop).
      // We still clear our local selectedUmat so UI resets.
      setSelectedUmat(null);
      return;
    }

    // set searchTerm to chineseName (auto-fill) if exists; otherwise empty
    const cname = attrs?.chineseName ?? attrs?.chinesename ?? "";
    setSearchTerm(cname);
    // trigger search immediately
  }, [selectedUmat]);

  // search qiu-dao-people when searchTerm changes
  useEffect(() => {
    let cancelled = false;
    async function search() {
      const q = (searchTerm || "").trim();
      if (!q) {
        setQiuResults([]);
        return;
      }

      // if q is short & not Chinese, require minimum length (follow QiuDaoBook pattern)
      const isChinese = /[\u3400-\u9FBF]/.test(q);
      if (!isChinese && q.length < 2) { // allow 2 chars here
        setQiuResults([]);
        return;
      }

      setLoading(true);
      try {
        // Build filters similar to QiuDaoBook but simpler: search qiuDaoName, yinShi, baoShi, address
        const params = new URLSearchParams();
        params.set("filters[$or][0][qiuDaoName][$containsi]", q);
        params.set("sort", "qiuDaoDate:desc");
        // we want first page only (reasonable)
        params.set("pagination[page]", "1");
        params.set("pagination[pageSize]", "50");

        const path = `/api/qiu-dao-people?${params.toString()}`;
        const json = await apiFetch(path);

        if (cancelled) return;

        const arr = json?.data ?? [];
        // Filter out qiu persons that already linked to an umat (we only want unlinked persons).
        const filtered = (arr || []).filter(item => {
          const a = item.attributes ?? item;
          // if 'umat' attribute exists and not null => already linked
          if (a?.umat) return false;
          return true;
        });

        setQiuResults(filtered);
        setError(null);
      } catch (err) {
        console.error("search qiu", err);
        setError("Gagal mencari QiuDao-person: " + (err?.message ?? String(err)));
        setQiuResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    search();
    return () => { cancelled = true; };
  }, [searchTerm]);

  // submit mapping
  async function handleSubmit(e) {
    e && e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitState("submitting");

    if (!selectedUmat) return setError("Pilih umat terlebih dahulu.");
    if (!selectedQiu) return setError("Pilih satu QiuDao-person dari hasil pencarian.");

    // get documentId / id for both items
    const umatId = selectedUmat.documentId ?? null;
    const qiuId = selectedQiu.documentId ?? null;

    if (!umatId || !qiuId) return setError("Data tidak lengkap (id tidak ditemukan).");

// build payload for umat-updates (same shape as UmatUpdateIndividual)
    const payload = {
      data: {
        type: "mappingQiuDao",
        updateStatus: canEditAndApply ? "Applied" : "Pending",
        targetUmats: [umatId],
        updatePayload: { qiu_dao_person: qiuId },
        notes: canEditAndApply ? "Mapping QiuDao applied by approver" : "Mapping QiuDao submitted (pending approval)",
        recordOwner: user?.username ?? null,
        recordEditor: user?.username ?? null,
        recordApprover: canEditAndApply ? (user?.username ?? null) : null
      }
    };

    setLoading(true);
    try {
      // If user can apply immediately, first attempt to PUT umats/{id} (apply relation)
      let applied = false;
      let appliedResult = { success: false, detail: null };

      if (canEditAndApply) {
        let resp;

        try {
          resp = await apiFetch(`/api/qiu-dao-people/${qiuId}`, {
            method: "PUT",
            body: { data: { umat: umatId } }
          });

          if (!resp || resp.error) {
            throw new Error("Apply failed - invalid response");
          }

          appliedResult = {
            success: true,
            detail: "Mapping applied to QiuDaoPerson"
          };

        } catch (err) {
          appliedResult = {
            success: false,
            detail: err?.message ?? String(err)
          };
        }
      }

      // Always create umat-updates record (Pending or Applied)
      try {
        await apiFetch("/api/umat-updates", {
          method: "POST",
          body: {
            data: {
              type: "mappingQiuDao",
              updateStatus: appliedResult.success ? "Applied" : "Pending",
              targetUmats: [umatId],
              updatePayload: { qiu_dao_person: qiuId },
              appliedStatus: appliedResult,
              notes: appliedResult.success
                ? "Mapping applied by approver"
                : `Apply failed: ${appliedResult.detail}`,
              recordOwner: user?.username ?? null,
              recordEditor: user?.username ?? null,
              recordApprover: canEditAndApply ? user?.username : null
            }
          }
        });
      } catch (createErr) {
        // if create fails but we applied above, we should report but continue
        console.error("create umat-updates failed", createErr);
        if (appliedResult.success) {
          setMessage({ type: "warning", text: "Mapping diterapkan ke Umat, tetapi penyimpanan di umat-updates gagal: " + (createErr?.message ?? String(createErr)) });
          onSaved && onSaved(null);
          return;
        }
        throw createErr;
      }

      if (canEditAndApply) {
        if (appliedResult.success) {
          setMessage({
            type: "success",
            text: "Mapping berhasil diterapkan dan disimpan (Applied)."
          });
          setSubmitState("success");
        } else {
          setMessage({
            type: "error",
            text: `Gagal menerapkan mapping: ${appliedResult.detail}. Disimpan sebagai pending.`
          });
          setSubmitState("error");
        }
      } else {
        // hanya submit (pending)
        setMessage({
          type: "success",
          text: "Permintaan mapping tersimpan dan menunggu approval."
        });
        setSubmitState("success");
      }

      // reset selection after success
      setSelectedUmat(null);
      setSearchTerm("");
      setQiuResults([]);
      setSelectedQiu(null);

      onSaved && onSaved({ umatId, qiuId, applied: appliedResult.success });
    } catch (err) {
      console.error("submit mapping error", err);
      setError("Gagal menyimpan mapping: " + (err?.message ?? String(err)));
    } finally {
      setLoading(false);
    }
  }

  // small presentational card similar to QiuDaoBook CardQiuDaoPerson (compact)
  function Card({ item, highlight, onChoose, chosen }) {
    const a = item.attributes ?? item;
    const gender = a?.gender ?? "";
    const isMale = gender === "乾" || gender === "童";
    const bgColor = isMale ? "#e0f2fe" : "#fee2e2";
    const ageText = a?.qiuDaoAgeText ?? "";

    return (
      <div
        onClick={() => onChoose && onChoose(item)}
        style={{
          border: chosen ? `2px solid #0b5ed7` : "1px solid #e5e7eb",
          borderRadius: rem(8),
          padding: rem(10),
          marginBottom: rem(10),
          cursor: "pointer",
          background: "#fff",
          boxShadow: chosen ? "0 6px 18px rgba(11,94,215,0.06)" : "none"
        }}
      >
        <div style={{ fontSize: rem(14), fontWeight: 700 }}>
          {a?.qiuDaoYear ? a.qiuDaoYear : ""} - {a?.qiuDaoLocation ?? ""}
        </div>
        <div style={{ marginTop: rem(6), fontSize: rem(15), background: bgColor, padding: rem(6), borderRadius: rem(6) }}>
          {a?.qiuDaoName} <span style={{ paddingLeft: rem(8), fontWeight: 600 }}>{gender ? `(${gender} - ${ageText})` : `(${ageText})`}</span>
        </div>
        <div style={{ marginTop: rem(6), color: "#6b7280", fontSize: rem(13) }}>
          {a?.phone ?? ""} — {a?.address ?? ""}
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontSize: rem(14) }}>
      <div style={{ marginBottom: rem(8) }}>
        <label style={{ display: "block", marginBottom: rem(6), color: "#2b4a84", fontWeight: 700 }}>Pilih Umat (single)</label>
        <LookupUmat
          value={selected}
          onChange={(v) => {
            if (lockedKodeUmat) return;
            // LookupUmat kadang mengirim array (multiple) atau object (single) tergantung impl.
            // Normalisasi: ambil element pertama jika array, null jika array kosong.
            const normalized = Array.isArray(v) ? (v.length ? v[0] : null) : v;

            setSelected(v);           // tetap set state yang dipakai LookupUmat UI
            setSelectedUmat(normalized); // set state yang dipakai logic mapping & auto-fill

            // ✅ reset message saat user mulai aksi baru
            setMessage(null);
            setError(null);
          }}
          initialValue={lockedKodeUmat ? { umatID: lockedKodeUmat } : undefined}
          disabled={!!lockedKodeUmat || submitState === "success"}
          linkedField = "qiu_dao_person"
        />
        {lockedKodeUmat && <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>KodeUmat dikunci: {lockedKodeUmat}</div>}

      </div>

      {error && <div style={{ color: "crimson", marginBottom: rem(8) }}>{error}</div>}
      {message && (
        <div style={{
          marginBottom: rem(8),
          padding: rem(8),
          borderRadius: rem(8),
          background: message.type === "success" ? "#d1e7dd" : (message.type === "warning" ? "#fff3cd" : "#f8d7da"),
          color: message.type === "success" ? "#0f5132" : "#664d03"
        }}>
          {message.text}
        </div>
      )}

      <div style={{ marginBottom: rem(8) }}>
        <label style={{ display: "block", marginBottom: rem(6) }}>Cari QiuDao-person</label>
        <input
          ref={qiuBoxRef}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="ketik nama QiuDao atau biarkan otomatis dari Nama Tionghoa umat"
          style={{ padding: rem(8), borderRadius: rem(6), border: "1px solid #d1d5db", width: "60%" }}
        />
      </div>

      <div style={{ maxHeight: "50vh", overflow: "auto", padding: rem(6), borderRadius: rem(8), border: "1px solid #e6eef8", background: "#fff" }}>
        {loading && <div style={{ marginBottom: rem(6), color: "#6b7280" }}>Memuat...</div>}
        {!loading && qiuResults.length === 0 && <div style={{ color: "#666" }}>Tidak ada hasil.</div>}
        {!loading && qiuResults.map(q => (
          <Card
            key={q.documentId ?? q.id}
            item={q}
            onChoose={(it) => setSelectedQiu(it)}
            chosen={selectedQiu && (selectedQiu.documentId ?? selectedQiu.id) === (q.documentId ?? q.id)}
          />
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: rem(8), marginTop: rem(10) }}>
        <button
          type="button"
          onClick={() => {
            setSelected(null);
            setSelectedUmat(null);
            setSearchTerm("");
            setQiuResults([]);
            setSelectedQiu(null);
            setMessage(null);
            setError(null);
            setSubmitState("idle"); // ✅ penting
          }}
          style={{
            padding: `${rem(8)} ${rem(12)}`,
            borderRadius: rem(8),
            border: "1px solid #d1d5db",
            background: "#fff",
            cursor: "pointer"
          }}
        >
          Reset
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={
            submitState === "submitting" ||
            submitState === "success" || // ✅ disable setelah sukses
            loading ||
            !selectedUmat ||
            !selectedQiu ||
            alreadyLinked ||
            (!canEdit && !canEditAndApply)
          }
          style={{
            padding: `${rem(8)} ${rem(12)}`,
            borderRadius: rem(8),
            border: "none",
          background:
            submitState === "success"
              ? "#16a34a" // hijau success
              : (!canEdit && !canEditAndApply)
              ? "#9ca3af"
              : "#2b4a84",
          color: "#fff",
          cursor:
            submitState === "submitting"
              ? "wait"
              : (submitState === "success" ? "not-allowed" : "pointer")
          }}
        >
          {submitState === "submitting"
            ? "Applying..."
            : submitState === "success"
            ? "Applied"
            : (canEditAndApply ? "Apply & Save" : "Submit Mapping")}
        </button>
      </div>
    </div>
  );
}
